import React from 'react';
import { useSelector } from 'react-redux';
import { getOrLoadUserDetails } from 'modules/contacts/stores/contacts-slice-handler';
import ContactUserPicture from './contact-user-picture';
import Preloader from 'modules/shared/components/preloader';
import classNames from 'classnames';
import Tooltip from '@mui/material/Tooltip';
import { getOperationalStatusById } from '../../settings/stores/company-settings-slice-handler';
import ContactMenu from './contact-menu';
import { ContactMenuType, UserType } from '../../../constants/contacts-constants';
import LabelWithCheckbox from '../../shared/components/label-with-checkbox';
import t from 'utils/translate';

/**
 * Render the details of a user asynchronously.
 * @param {string} msisdn
 * @param {boolean} withStatusDetails If should render the statuses details: operational status (when active) and user status
 * @param {boolean} withFavoriteControls If should render the favorite controls
 * @param {ContactMenuType} menuType The type of the menu to be rendered. (optional)
 * @param {boolean} multiSelectActive If the multi-select mode is enabled or not. When enabled a checkbox is shown. (optional)
 * @param {boolean} multiSelectState The initial state of the multi-selection checkbox. (optional)
 * @param {boolean} multiSelectDisableState If the checkbox is enabled or not. (optional)
 * @param {Function} multiSelectOnChange A callback to be executed when the checkbox changes. (optional)
 * @param {Function} nameClickCallback A callback to be executed when the name of the contact is clicked. (optional)
 *                                     The callback will be called only if not multi-select mode is enabled.
 *                                     The callback will receive as argument the current contact.
 * @return {JSX.Element}
 * @constructor
 */
export default function ContactUserItem({
    msisdn,
    withStatusDetails,
    withFavoriteControls,
    menuType,
    multiSelectActive,
    multiSelectState,
    multiSelectDisableState,
    multiSelectOnChange,
    nameClickCallback,
    ...otherProps
}) {
    const user = useSelector(() => getOrLoadUserDetails(msisdn));
    const computedUserName = user ? user.ComputedUserName : msisdn;
    const operationalStatus = useSelector(() => getOperationalStatusById(user ? user.OperationalStatus : 0));

    const onElementClick = () => {
        if (multiSelectActive) {
            return;
        }

        if (nameClickCallback) {
            nameClickCallback(user);
        }
    };

    // user has been removed from the local cache (maybe by a CUG update)
    if (user === null) {
        return '';
    }

    let dataTestId = '';
    if ([UserType.GROUP, UserType.EXTERNAL_VIDEO_DESTINATION, UserType.EXTERNAL_VIDEO_SOURCE].includes(user._type)) {
        dataTestId = computedUserName;
    } else {
        dataTestId = msisdn;
    }

    return (
        <div
            className={classNames({
                'wcf-ui-contact-item': true,
                'contact-user-item': true,
                'with-checkbox': !!multiSelectActive,
                'with-favorite-star': withFavoriteControls === true && user.IsFavorite,
                disabled: !!multiSelectActive && !!multiSelectDisableState,
            })}
            data-test-id={`contacts-context-menu-${dataTestId}`}
            {...otherProps}
        >
            {user.IsUpdating && <Preloader asOverlay />}
            {multiSelectActive && (
                <div className={'wcf-ui-checkbox-container'} data-test-id={`checkbox-${dataTestId}`}>
                    <LabelWithCheckbox
                        labelContent={<div className={'multi-select-area'}></div>}
                        onChange={(e) => {
                            multiSelectOnChange(user, e.target.checked);
                        }}
                        checked={!!multiSelectState}
                        disabled={!!multiSelectDisableState}
                        variation={'round'}
                    ></LabelWithCheckbox>
                </div>
            )}
            <div className="user-picture-container" onClick={onElementClick}>
                <ContactUserPicture msisdn={msisdn} />
            </div>
            <div className={'user-details-container'} onClick={onElementClick}>
                <div className={'user-name-container'}>
                    <Tooltip title={computedUserName} arrow followCursor={true}>
                        <div className={'user-name'}>
                            {computedUserName}
                            {user.IsGuest && <span>({t('js_all_guest')})</span>}
                        </div>
                    </Tooltip>
                </div>
                {withStatusDetails === true && operationalStatus !== null && (
                    <div className={'operational-status-container'}>
                        <Tooltip
                            title={`${operationalStatus.Code} - ${operationalStatus.Label}`}
                            arrow
                            followCursor={true}
                        >
                            <div
                                className={'operational-status'}
                            >{`${operationalStatus.Code} - ${operationalStatus.Label}`}</div>
                        </Tooltip>
                    </div>
                )}
                {withStatusDetails === true && user.UserCustomStatus.length > 0 && (
                    <div className={'user-status-container'}>
                        <Tooltip title={`${user.UserCustomStatus}`} arrow followCursor={true}>
                            <div className={'user-status'}>{user.UserCustomStatus}</div>
                        </Tooltip>
                    </div>
                )}
            </div>
            {withFavoriteControls === true && user.IsFavorite && (
                <div className={'favorite-controls-container'} data-test-id={'favorite-controls-icon'}>
                    <span className={'stw-favorite-active'}></span>
                </div>
            )}
            {!multiSelectActive && menuType && <ContactMenu contact={user} menuType={menuType} />}
            {otherProps.children}
        </div>
    );
}
